import { BarChartOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons'
import {
  ReportReturnReadable,
  reportsCreateMutation,
  reportsListOptions,
  reportsListQueryKey,
  reportsTypesRetrieveOptions,
  reportsUpdateMutation
} from '@crm/api'
import { useStyles } from '@crm/features/reports/styles'
import { useAppNavigate } from '@crm/hooks/useAppNavigate'
import { paths } from '@crm/routes'
import { useInvalidatePredicateQueries } from '@shared/hooks'
import { useLocales } from '@shared/locales'
import { useMutation, useQuery } from '@tanstack/react-query'
import {
  Button,
  Col,
  Flex,
  Form,
  Input,
  Menu,
  Modal,
  notification,
  Row,
  Select,
  Space,
  Spin,
  Typography
} from 'antd'
import { useEffect, useMemo, useState } from 'react'
import { Outlet, useParams } from 'react-router-dom'

import type { MenuProps } from 'antd'

const { Title } = Typography

const formName = 'entity-form'

export const ReportsManagementLayout = () => {
  const navigate = useAppNavigate()
  const [form] = Form.useForm()
  const { id } = useParams()
  const { t } = useLocales()
  const { styles } = useStyles()

  const { invalidateQueries } = useInvalidatePredicateQueries()
  const [isOpen, setIsOpen] = useState(false)
  const [editIndex, setEditIndex] = useState<number>()

  const { data: reports, isLoading } = useQuery(reportsListOptions())

  useEffect(() => {
    if (reports?.length && !id) {
      navigate(paths.reportManagement.item(String(reports?.[0]?.id)))
    }
  }, [reports])

  const { mutate: onAdd } = useMutation({
    ...reportsCreateMutation(),
    onError: () =>
      notification.error({
        message: t('error.error'),
        description: t('error.something_is_wrong')
      }),
    onSuccess: ({ id }) => {
      invalidateQueries([reportsListQueryKey()])

      form.resetFields()
      setIsOpen(false)

      navigate(paths.reportManagement.item(String(id)))

      notification.success({
        message: t('success.success'),
        description: t('success.success_data_save')
      })
    }
  })

  const { mutate: onUpdate } = useMutation({
    ...reportsUpdateMutation(),
    onError: () =>
      notification.error({
        message: t('error.error'),
        description: t('error.something_is_wrong')
      }),
    onSuccess: () => {
      invalidateQueries([reportsListQueryKey()])

      form.resetFields()
      setIsOpen(false)
      setEditIndex(undefined)

      notification.success({
        message: t('success.success'),
        description: t('success.success_data_save')
      })
    }
  })

  const onEdit = (index: number) => {
    setEditIndex(index)
    form.setFieldsValue(reports?.find((_: unknown, i: number) => i === index))
    setIsOpen(true)
  }

  const isEdit = useMemo(() => typeof editIndex !== 'undefined', [editIndex])

  const items: MenuProps['items'] = reports?.map(
    ({ name, id }: ReportReturnReadable, index: number) => ({
      label: (
        <Flex style={{ height: '100%' }} align="center" justify="space-between">
          <Space align="center">
            <BarChartOutlined />
            {name}
          </Space>

          <Button
            size="small"
            icon={<EditOutlined />}
            onClick={(e) => {
              e.stopPropagation()
              onEdit(index)
            }}
          />
        </Flex>
      ),

      key: id || '',
      onClick: () => navigate(paths.reportManagement.item(String(id)))
    })
  )

  const { data: reportsTypes } = useQuery(reportsTypesRetrieveOptions())

  return (
    <Row gutter={16} wrap={false}>
      <Col>
        <Space align="start" className="mb-30">
          <Title className="mb-0" level={4}>
            {t('generic.report')}
          </Title>

          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setIsOpen(true)}
          />
        </Space>

        <Spin spinning={isLoading}>
          <Menu
            className={styles.sideMenu}
            mode="vertical"
            selectedKeys={[id || '']}
            items={items}
          />
        </Spin>
      </Col>

      <Col flex="auto">
        <Outlet />
      </Col>

      <Modal
        open={!!isOpen}
        title={t(isEdit ? 'edit_report' : 'add_report')}
        onCancel={() => {
          setIsOpen(false)
          setEditIndex(undefined)
          form.resetFields()
        }}
        cancelText={t('buttons.cancel')}
        okText={t('buttons.save')}
        okButtonProps={{ htmlType: 'submit', form: formName }}
        destroyOnHidden
      >
        <Form
          form={form}
          name={formName}
          layout="vertical"
          onFinish={({ chart_type, ...values }) =>
            isEdit
              ? onUpdate({
                  path: {
                    id: reports![editIndex!].id
                  },
                  body: {
                    ...values,
                    ...(chart_type && {
                      chart_type: chart_type === 'null' ? null : chart_type
                    })
                  }
                })
              : onAdd({
                  body: {
                    ...values,
                    ...(chart_type && {
                      chart_type: chart_type === 'null' ? null : chart_type
                    })
                  }
                })
          }
        >
          <Form.Item name="name" label="Name" rules={[{ required: true }]}>
            <Input />
          </Form.Item>

          {!isEdit && (
            <>
              <Form.Item
                name="instance_type"
                label="Type"
                rules={[{ required: true }]}
              >
                <Select
                  options={reportsTypes?.results.map((type) => ({
                    value: type,
                    label: type
                  }))}
                />
              </Form.Item>

              <Form.Item
                name="chart_type"
                label="Chart type"
                initialValue="null"
              >
                <Select
                  options={[
                    { value: 'null', label: 'Empty' },
                    { value: 'pie', label: 'Pie' },
                    { value: 'bar', label: 'Bar' },
                    { value: 'line', label: 'Line' },
                    { value: 'funnel', label: 'Funnel' }
                  ]}
                />
              </Form.Item>
            </>
          )}
        </Form>
      </Modal>
    </Row>
  )
}
